import {
  PRODUCT_CATEGORIES,
  PRODUCT_CATEGORY_LABELS,
  PRODUCT_MENU_GROUPS,
  PRODUCT_MENU_GROUP_LABELS,
  type Product,
} from "@artisancode/api-types";
import { Plus, Pencil, History, Image as ImageIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import type { Column, FilterOption } from "@/components/shared/data-table";
import { DataTable } from "@/components/shared/data-table";
import { PageHeader } from "@/components/shared/page-header";
import { StatusToggleBadge } from "@/components/shared/status-toggle-badge";
import { ZoomableImage } from "@/components/shared/zoomable-image";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useHasPermission, useMe } from "@/hooks/use-auth";
import { useUpdateProduct } from "@/hooks/use-products";
import { useServerTable } from "@/hooks/use-server-table";
import { useWorkspaceFilterOption } from "@/hooks/use-workspace-filter";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { queryKeys } from "@/lib/query-keys";
import { productService } from "@/services/product";

import { ProductHistoryDialog } from "./item-history-dialog";
import { ProductDialog } from "./product-dialog";

const productFilters: FilterOption[] = [
  {
    key: "category",
    label: "Kategori",
    options: PRODUCT_CATEGORIES.map((c) => ({
      value: c,
      label: PRODUCT_CATEGORY_LABELS[c],
    })),
  },
  {
    key: "menu_group",
    label: "Grup Menu",
    options: PRODUCT_MENU_GROUPS.map((g) => ({
      value: g,
      label: PRODUCT_MENU_GROUP_LABELS[g],
    })),
  },
];

export function Products() {
  const canCreate = useHasPermission("products.create");
  const canUpdate = useHasPermission("products.update");

  const { mutate: update } = useUpdateProduct();
  const { data: me } = useMe();
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = workspacesData?.items ?? [];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [historyOf, setHistoryOf] = useState<Product | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [viewingPhoto, setViewingPhoto] = useState<{
    url: string;
    name: string;
  } | null>(null);

  const workspaceFilter = useWorkspaceFilterOption();
  const table = useServerTable<Product>({
    queryKey: (params) => queryKeys.products.list(params),
    fetcher: (params) => productService.list(params),
    pageSize: 10,
    filterKeys: ["category", "menu_group", "workspace_id"],
  });

  function toggleActive(item: Product) {
    if (!canUpdate || updatingId === item.id) return;
    setUpdatingId(item.id);
    update(
      { id: item.id, isActive: !item.isActive },
      {
        onSuccess: () =>
          toast.success(item.isActive ? "Dinonaktifkan." : "Diaktifkan."),
        onError: () => toast.error("Gagal mengubah status produk."),
        onSettled: () => setUpdatingId(null),
      },
    );
  }

  function workspaceName(workspaceId: string) {
    return workspaces.find((w) => w.id === workspaceId)?.name ?? "-";
  }

  const columns: Column<Product>[] = [
    {
      key: "name",
      label: "Nama Produk",
      render: (i) => (
        <div className="flex items-center gap-3">
          {i.photoUrl ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (i.photoUrl) {
                  setViewingPhoto({ url: i.photoUrl, name: i.name });
                }
              }}
              title="Lihat foto produk"
              className="relative h-11 w-11 shrink-0 overflow-hidden rounded-lg border bg-muted focus:outline-none focus:ring-2 focus:ring-primary/50 group"
            >
              <img
                src={i.photoUrl}
                alt={i.name}
                className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-150"
              />
            </button>
          ) : (
            <div className="h-11 w-11 shrink-0 rounded-lg border border-dashed bg-muted/40 flex items-center justify-center text-muted-foreground/40">
              <ImageIcon className="h-5 w-5" />
            </div>
          )}
          <div className="flex flex-col min-w-0">
            <span className="font-medium truncate">{i.name}</span>
            {i.sku && (
              <span className="font-mono text-xs text-muted-foreground">
                {i.sku}
              </span>
            )}
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-xs text-muted-foreground">
                {PRODUCT_CATEGORY_LABELS[i.category] ?? i.category}
              </span>
              <span className="text-muted-foreground">•</span>
              <span className="text-[11px] font-medium">
                {PRODUCT_MENU_GROUP_LABELS[i.menuGroup]}
              </span>
              <span className="text-muted-foreground">•</span>
              {i.sourcingType === "produced" ? (
                <span className="text-[11px] text-primary font-medium">
                  Racikan / Resep
                </span>
              ) : (
                <span className="text-[11px] text-muted-foreground font-medium">
                  Beli Jadi
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "sellingPrice",
      label: "Harga Jual",
      render: (i) => (i.sellingPrice ?? 0).toLocaleString("id-ID"),
    },
    {
      key: "cogs",
      label: "COGS / HPP",
      render: (i) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {(i.cogs ?? 0).toLocaleString("id-ID", {
              maximumFractionDigits: 4,
            })}
          </span>
          {i.cogsWarning === "INCOMPLETE_COGS" ? (
            <span className="text-[11px] text-amber-600 dark:text-amber-400">
              HPP belum lengkap: {i.missingCostItems.join(", ")} belum ada harga
            </span>
          ) : i.sourcingType === "produced" && (!i.cogs || i.cogs === 0) ? (
            <span className="text-[11px] text-amber-600 dark:text-amber-400">
              Resep belum dibuat
            </span>
          ) : i.cogsWarning === "MISSING_COGS" ? (
            <span className="text-[11px] text-destructive">
              Belum ada HPP (isi lewat Stok Awal atau belanja)
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: "minStock",
      label: "Min. Stok",
      render: (i) =>
        i.minStock != null ? `${i.minStock.toLocaleString("id-ID")} pcs` : "-",
    },
    ...(me?.canSwitchWorkspace
      ? [
          {
            key: "workspace",
            label: "Workspace",
            render: (i: Product) => (
              <span className="text-sm">{workspaceName(i.workspaceId)}</span>
            ),
          },
        ]
      : []),
    {
      key: "status",
      label: "Status",
      render: (i) => (
        <StatusToggleBadge
          isActive={i.isActive}
          isLoading={updatingId === i.id}
          disabled={!canUpdate}
          onClick={() => toggleActive(i)}
        />
      ),
    },
  ];

  function openAdd() {
    setEditing(null);
    setOpen(true);
  }

  function openEdit(item: Product) {
    setEditing(item);
    setOpen(true);
  }

  return (
    <div>
      <PageHeader
        title="Produk"
        action={
          <Button size="sm" onClick={openAdd} disabled={!canCreate}>
            <Plus className="mr-1 h-4 w-4" />
            Tambah
          </Button>
        }
      />
      <DataTable
        data={table.items}
        loadedData={table.loadedItems}
        columns={columns}
        searchPlaceholder="Cari produk..."
        query={table.query}
        onQueryChange={table.onQueryChange}
        filters={[...workspaceFilter, ...productFilters]}
        activeFilters={table.activeFilters}
        onFilterChange={table.onFilterChange}
        page={table.page}
        totalPages={table.totalPages}
        totalCount={table.totalCount}
        onPageChange={table.onPageChange}
        hasMore={table.hasMore}
        onLoadMore={table.onLoadMore}
        loading={table.loading}
        actions={(item) => (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openEdit(item)}
              disabled={!canUpdate}
              title="Edit Produk"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setHistoryOf(item)}
              title="Riwayat harga"
            >
              <History className="h-4 w-4" />
            </Button>
          </div>
        )}
      />

      <ProductDialog open={open} onOpenChange={setOpen} editing={editing} />
      <ProductHistoryDialog
        item={historyOf}
        onOpenChange={(v) => !v && setHistoryOf(null)}
      />

      {viewingPhoto && (
        <Dialog
          open={!!viewingPhoto}
          onOpenChange={(v) => !v && setViewingPhoto(null)}
        >
          <DialogContent className="max-w-md p-4">
            <DialogHeader>
              <DialogTitle className="text-base truncate">
                {viewingPhoto.name}
              </DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center overflow-hidden rounded-lg bg-black/5 dark:bg-black/40">
              <ZoomableImage
                src={viewingPhoto.url}
                alt={viewingPhoto.name}
                className="max-h-[70vh] w-auto object-contain rounded-md"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
